import { useSubjectPhotoCounts } from 'features/fim/photos/useSubjectPhotoCounts'
import {
  getPageLayout,
  rectGeometry,
} from 'features/knowledgeDocumentViewer/components/viewer/search/aliasHighlightGeometry'
import { TimeSeriesIndicatorIcon } from 'features/knowledgeDocumentViewer/components/viewer/shared/TimeSeriesIndicatorIcon'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useInstrumentSelectionOptional } from 'features/knowledgeDocumentViewer/contexts/InstrumentSelectionContext'
import { useTimeSeriesLinkContext } from 'features/knowledgeDocumentViewer/contexts/TimeSeriesLinkContext'
import {
  type CanvasRect,
  canvasRectFromBox,
  rectsOverlap,
} from 'features/knowledgeDocumentViewer/graph/graphInstrumentation'
import type { AliasHighlight } from 'features/knowledgeDocumentViewer/types/documentSubjects'
import { matchSubjectDatapoints } from 'features/knowledgeDocumentViewer/utils/instrumentTagUtils'
import React, { useMemo } from 'react'

type AliasHighlightOverlayProps = {
  graphLinkedRects?: CanvasRect[]
  imageNatural: { w: number; h: number }
  firstPageWidth?: number
  isMultipage?: boolean
  /** Paint fills, outlines, and the time series marker. */
  showHighlights: boolean
  /** Answer the pointer: click, hover tooltips, and the pointer cursor. */
  interactive: boolean
  photoMode?: boolean
  /** Tags already showing a live reading, which marks them better than this. */
  labelsWithReadings?: ReadonlySet<string>
  onAliasClick?: (highlight: AliasHighlight, event?: React.MouseEvent) => void
  onAliasHover?: (
    highlight: AliasHighlight | null,
    event?: React.MouseEvent
  ) => void
}

type HighlightTone = 'subject' | 'photo' | 'selected'

const TONES: Record<
  HighlightTone,
  { fill: string; fillOpacity: number; stroke: string; strokeWidth: number }
> = {
  subject: {
    fill: 'rgb(168, 85, 247)',
    fillOpacity: 0.25,
    stroke: 'rgba(168, 85, 247, 0.9)',
    strokeWidth: 1.5,
  },
  photo: {
    fill: 'rgb(249, 115, 22)',
    fillOpacity: 0.25,
    stroke: 'rgba(249, 115, 22, 0.9)',
    strokeWidth: 1.5,
  },
  selected: {
    fill: 'rgb(34, 197, 94)',
    fillOpacity: 0.3,
    stroke: 'rgba(34, 197, 94, 1)',
    strokeWidth: 2.5,
  },
}

type AliasRectGeometry = {
  key: string
  highlight: AliasHighlight
  tone: HighlightTone
  hasTimeSeries: boolean
  x: number
  y: number
  width: number
  height: number
}

export function AliasHighlightOverlay({
  graphLinkedRects = [],
  imageNatural,
  firstPageWidth,
  isMultipage,
  showHighlights,
  interactive,
  photoMode = false,
  labelsWithReadings,
  onAliasClick,
  onAliasHover,
}: AliasHighlightOverlayProps) {
  const {
    aliasHighlights,
    pageNumber,
    documentSubjects,
    panelSubjectUid,
    siteID,
  } = useDocumentTabDataContext()
  const instrumentSelection = useInstrumentSelectionOptional()
  const { getMatchedDatapoints, getDatapointsByIds } =
    useTimeSeriesLinkContext()
  const photoCounts = useSubjectPhotoCounts(siteID, documentSubjects)

  // The subject open in the sidebar panel counts as selected alongside any
  // instrument multi-select, so its boxes stay visible even with the asset
  // highlight setting off.
  const selectedSubjectUids = useMemo(() => {
    const uids = new Set<string>()
    if (panelSubjectUid) uids.add(panelSubjectUid)
    for (const instrument of instrumentSelection?.selected.values() ?? []) {
      if (instrument.subjectUid) uids.add(instrument.subjectUid)
    }
    return uids
  }, [instrumentSelection?.selected, panelSubjectUid])

  const subjectUidsWithTimeSeries = useMemo(() => {
    const uids = new Set<string>()
    for (const subject of documentSubjects) {
      if (
        matchSubjectDatapoints(
          subject,
          getMatchedDatapoints,
          getDatapointsByIds
        ).length > 0
      ) {
        uids.add(subject.subject_uid)
      }
    }
    return uids
  }, [documentSubjects, getMatchedDatapoints, getDatapointsByIds])

  const visibleHighlights = aliasHighlights.filter(
    (highlight) =>
      highlight.page === pageNumber ||
      (isMultipage && highlight.page === pageNumber + 1)
  )

  if (visibleHighlights.length === 0) return null

  const toneFor = (highlight: AliasHighlight): HighlightTone => {
    if (selectedSubjectUids.has(highlight.subjectUid)) return 'selected'
    if (!photoMode && photoCounts.has(highlight.subjectUid)) return 'photo'
    return 'subject'
  }

  const rects: AliasRectGeometry[] = visibleHighlights.map((highlight, idx) => {
    const isRightPage = isMultipage === true && highlight.page > pageNumber
    const { pageWidth, xOffset } = getPageLayout(
      isRightPage,
      imageNatural,
      firstPageWidth,
      isMultipage
    )

    return {
      key: `${highlight.subjectUid}-${highlight.aliasNorm}-${idx}`,
      highlight,
      tone: toneFor(highlight),
      hasTimeSeries: subjectUidsWithTimeSeries.has(highlight.subjectUid),
      ...rectGeometry(highlight.bbox, pageWidth, xOffset, imageNatural.h),
    }
  })

  // Fills paint inside groups carrying the opacity, so overlapping highlights
  // composite once as a group instead of each rect darkening the one beneath.
  const fillLayer = (tone: HighlightTone) => {
    const layerRects = rects.filter((rect) => rect.tone === tone)
    return (
      layerRects.length > 0 && (
        <g
          data-tone={tone}
          opacity={TONES[tone].fillOpacity}
          style={{ pointerEvents: 'none' }}>
          {layerRects.map((rect) => (
            <rect
              key={rect.key}
              x={rect.x}
              y={rect.y}
              width={rect.width}
              height={rect.height}
              rx={2}
              ry={2}
              fill={TONES[tone].fill}
            />
          ))}
        </g>
      )
    )
  }

  return (
    <g style={{ isolation: 'isolate' }}>
      {showHighlights && fillLayer('subject')}
      {showHighlights && fillLayer('photo')}
      {fillLayer('selected')}
      {rects.map((rect) => (
        <React.Fragment key={rect.key}>
          <rect
            data-alias-label={rect.highlight.label}
            data-tone={rect.tone}
            x={rect.x}
            y={rect.y}
            width={rect.width}
            height={rect.height}
            rx={2}
            ry={2}
            fill='transparent'
            stroke={
              rect.tone === 'selected' || showHighlights
                ? TONES[rect.tone].stroke
                : 'none'
            }
            strokeWidth={TONES[rect.tone].strokeWidth}
            style={{
              cursor: interactive ? 'pointer' : 'default',
              pointerEvents: interactive ? 'auto' : 'none',
            }}
            onClick={
              interactive
                ? (event) => onAliasClick?.(rect.highlight, event)
                : undefined
            }
            onMouseEnter={
              interactive
                ? (event) => onAliasHover?.(rect.highlight, event)
                : undefined
            }
            onMouseLeave={interactive ? () => onAliasHover?.(null) : undefined}
          />
          {showHighlights &&
            !photoMode &&
            rect.hasTimeSeries &&
            !graphLinkedRects.some((graphRect) =>
              rectsOverlap(graphRect, canvasRectFromBox(rect))
            ) &&
            !labelsWithReadings?.has(rect.highlight.label) && (
              <TimeSeriesIndicatorIcon
                x={rect.x + rect.width - 6}
                y={rect.y - 10}
                size={14}
              />
            )}
        </React.Fragment>
      ))}
    </g>
  )
}
